import { useState, useEffect } from 'preact/hooks';
import { superAdminFetch, superAdminPost, superAdminPut, superAdminDelete } from '../../lib/super-admin-api';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.unplug-app.com';

export function SuperAdminPlans() {
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [newPlan, setNewPlan] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      const [plansRes, featuresRes] = await Promise.all([
        superAdminFetch(`${API_URL}/api/super-admin/plans`),
        superAdminFetch(`${API_URL}/api/super-admin/features`)
      ]);

      if (!plansRes.ok || !featuresRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const plansData = await plansRes.json();
      const featuresData = await featuresRes.json();

      setPlans(plansData);
      setFeatures(featuresData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSavePlan(plan) {
    setSaving(true);
    try {
      // Update plan details
      await superAdminPut(`${API_URL}/api/super-admin/plans/${plan.id}`, {
        name: plan.name,
        description: plan.description,
        price_monthly: parseFloat(plan.price_monthly) || 0,
        price_yearly: parseFloat(plan.price_yearly) || 0,
        seats_limit: parseInt(plan.seats_limit) || 0,
        is_active: plan.is_active
      });

      // Update plan features
      await superAdminPut(`${API_URL}/api/super-admin/plans/${plan.id}/features`, {
        feature_ids: plan.features.map(f => f.id)
      });

      setEditingPlan(null);
      await fetchData();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  function toggleFeature(plan, feature, isNew = false) {
    const hasFeature = plan.features.some(f => f.id === feature.id);
    const updatedFeatures = hasFeature
      ? plan.features.filter(f => f.id !== feature.id)
      : [...plan.features, feature];

    if (isNew) {
      setNewPlan({
        ...plan,
        features: updatedFeatures
      });
    } else {
      setEditingPlan({
        ...plan,
        features: updatedFeatures
      });
    }
  }

  function startNewPlan() {
    setNewPlan({
      key: '',
      name: '',
      description: '',
      price_monthly: 0,
      price_yearly: 0,
      seats_limit: 10,
      is_active: 1,
      features: []
    });
    setEditingPlan(null);
  }

  async function handleCreatePlan(plan) {
    setSaving(true);
    try {
      // Create the plan
      const res = await superAdminPost(`${API_URL}/api/super-admin/plans`, {
        key: plan.key,
        name: plan.name,
        description: plan.description,
        price_monthly: parseFloat(plan.price_monthly) || 0,
        price_yearly: parseFloat(plan.price_yearly) || 0,
        seats_limit: parseInt(plan.seats_limit) || 0,
        is_active: plan.is_active ? 1 : 0
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to create plan');
      }

      const newPlanData = await res.json();

      // Update plan features if any selected
      if (plan.features && plan.features.length > 0) {
        await superAdminPut(`${API_URL}/api/super-admin/plans/${newPlanData.id}/features`, {
          feature_ids: plan.features.map(f => f.id)
        });
      }

      setNewPlan(null);
      await fetchData();
    } catch (err) {
      alert('Failed to create plan: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDeletePlan(plan) {
    if (!confirm(`Are you sure you want to delete the "${plan.name}" plan? This cannot be undone.`)) {
      return;
    }

    try {
      const res = await superAdminDelete(`${API_URL}/api/super-admin/plans/${plan.id}`);

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to delete plan');
      }

      await fetchData();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  }

  async function handleReorder(planId, direction) {
    const currentIndex = plans.findIndex(p => p.id === planId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= plans.length) return;

    // Create new order array
    const newPlans = [...plans];
    const [moved] = newPlans.splice(currentIndex, 1);
    newPlans.splice(newIndex, 0, moved);

    // Update local state immediately for responsiveness
    setPlans(newPlans);

    // Send reorder request to API
    try {
      const res = await superAdminPut(`${API_URL}/api/super-admin/plans/reorder`, {
        order: newPlans.map(p => p.id)
      });

      if (!res.ok) {
        // Revert on failure
        await fetchData();
        const errData = await res.json();
        alert('Failed to reorder: ' + errData.error);
      }
    } catch (err) {
      await fetchData();
      alert('Failed to reorder: ' + err.message);
    }
  }

  if (loading) {
    return (
      <div class="super-admin-page">
        <div class="loading-state">
          <div class="spinner" />
          <p>Loading plans...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div class="super-admin-page">
        <div class="error-state">
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const currentPlan = editingPlan || null;

  return (
    <div class="super-admin-page">
      <div class="page-header">
        <div>
          <h1>Plans & Features</h1>
          <p class="text-muted">Configure pricing plans and their features</p>
        </div>
        <div class="header-actions">
          <a href="/super-admin" class="btn btn-secondary">Back to Dashboard</a>
          <a href="/super-admin/features" class="btn btn-secondary">Manage Features</a>
          <button class="btn btn-primary" onClick={startNewPlan} disabled={newPlan !== null}>
            Create Plan
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div class="plans-grid">
        {/* New Plan Card */}
        {newPlan && (
          <div class="plan-card editing new-plan">
            <div class="plan-header">
              <input
                type="text"
                class="plan-name-input"
                value={newPlan.name}
                onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
                placeholder="Plan Name"
              />
              <span class="badge badge-info">New</span>
            </div>

            <div class="plan-key">
              <label>Key (unique identifier)</label>
              <input
                type="text"
                value={newPlan.key}
                onChange={(e) => setNewPlan({ ...newPlan, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                placeholder="e.g., enterprise"
              />
            </div>

            <div class="plan-description">
              <label>Description</label>
              <input
                type="text"
                value={newPlan.description}
                onChange={(e) => setNewPlan({ ...newPlan, description: e.target.value })}
                placeholder="Brief description of the plan"
              />
            </div>

            <div class="plan-pricing">
              <div class="price-inputs">
                <div class="price-input-group">
                  <label>Monthly</label>
                  <div class="input-with-prefix">
                    <span>EUR</span>
                    <input
                      type="number"
                      value={newPlan.price_monthly}
                      onChange={(e) => setNewPlan({ ...newPlan, price_monthly: e.target.value })}
                    />
                  </div>
                </div>
                <div class="price-input-group">
                  <label>Yearly</label>
                  <div class="input-with-prefix">
                    <span>EUR</span>
                    <input
                      type="number"
                      value={newPlan.price_yearly}
                      onChange={(e) => setNewPlan({ ...newPlan, price_yearly: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="plan-seats">
              <div class="seats-input-group">
                <label>Max Users</label>
                <input
                  type="number"
                  value={newPlan.seats_limit}
                  onChange={(e) => setNewPlan({ ...newPlan, seats_limit: e.target.value })}
                  placeholder="0 = unlimited"
                />
                <span class="hint">0 = unlimited</span>
              </div>
            </div>

            <div class="plan-features">
              <h4>Features ({newPlan.features?.length || 0})</h4>
              <div class="feature-list">
                {features.map(feature => {
                  const hasFeature = newPlan.features?.some(f => f.id === feature.id);
                  return (
                    <label key={feature.id} class={`feature-item ${hasFeature ? 'active' : ''}`}>
                      <input
                        type="checkbox"
                        checked={hasFeature}
                        onChange={() => toggleFeature(newPlan, feature, true)}
                      />
                      <span class="feature-name">{feature.name}</span>
                      <span class={`feature-category cat-${feature.category}`}>{feature.category}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div class="plan-actions">
              <button
                class="btn btn-primary"
                onClick={() => handleCreatePlan(newPlan)}
                disabled={saving || !newPlan.key || !newPlan.name}
              >
                {saving ? 'Creating...' : 'Create Plan'}
              </button>
              <button
                class="btn btn-secondary"
                onClick={() => setNewPlan(null)}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {plans.map((plan, index) => {
          const isEditing = editingPlan?.id === plan.id;
          const displayPlan = isEditing ? editingPlan : plan;

          return (
            <div key={plan.id} class={`plan-card ${isEditing ? 'editing' : ''}`}>
              {/* Reorder Controls */}
              <div class="reorder-controls">
                <button
                  class="reorder-btn"
                  onClick={() => handleReorder(plan.id, 'up')}
                  disabled={index === 0 || isEditing}
                  title="Move up"
                >
                  ▲
                </button>
                <span class="sort-index">{index + 1}</span>
                <button
                  class="reorder-btn"
                  onClick={() => handleReorder(plan.id, 'down')}
                  disabled={index === plans.length - 1 || isEditing}
                  title="Move down"
                >
                  ▼
                </button>
              </div>

              <div class="plan-header">
                {isEditing ? (
                  <input
                    type="text"
                    class="plan-name-input"
                    value={displayPlan.name}
                    onChange={(e) => setEditingPlan({ ...displayPlan, name: e.target.value })}
                  />
                ) : (
                  <h3>{plan.name}</h3>
                )}
                {plan.is_default === 1 && <span class="badge badge-primary">Default</span>}
              </div>

              <div class="plan-pricing">
                {isEditing ? (
                  <div class="price-inputs">
                    <div class="price-input-group">
                      <label>Monthly</label>
                      <div class="input-with-prefix">
                        <span>EUR</span>
                        <input
                          type="number"
                          value={displayPlan.price_monthly}
                          onChange={(e) => setEditingPlan({ ...displayPlan, price_monthly: e.target.value })}
                        />
                      </div>
                    </div>
                    <div class="price-input-group">
                      <label>Yearly</label>
                      <div class="input-with-prefix">
                        <span>EUR</span>
                        <input
                          type="number"
                          value={displayPlan.price_yearly}
                          onChange={(e) => setEditingPlan({ ...displayPlan, price_yearly: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div class="price">
                      {plan.price_monthly === 0 ? 'Free' : `EUR ${plan.price_monthly}/mo`}
                    </div>
                    {plan.price_yearly > 0 && (
                      <div class="price-yearly">EUR {plan.price_yearly}/year</div>
                    )}
                  </>
                )}
              </div>

              <div class="plan-seats">
                {isEditing ? (
                  <div class="seats-input-group">
                    <label>Max Users</label>
                    <input
                      type="number"
                      value={displayPlan.seats_limit}
                      onChange={(e) => setEditingPlan({ ...displayPlan, seats_limit: e.target.value })}
                      placeholder="0 = unlimited"
                    />
                    <span class="hint">0 = unlimited</span>
                  </div>
                ) : (
                  <span>{plan.seats_limit === 0 ? 'Unlimited users' : `Up to ${plan.seats_limit} users`}</span>
                )}
              </div>

              <div class="plan-features">
                <h4>Features ({displayPlan.features?.length || 0})</h4>
                <div class="feature-list">
                  {features.map(feature => {
                    const hasFeature = displayPlan.features?.some(f => f.id === feature.id);
                    return (
                      <label key={feature.id} class={`feature-item ${hasFeature ? 'active' : ''}`}>
                        <input
                          type="checkbox"
                          checked={hasFeature}
                          disabled={!isEditing}
                          onChange={() => toggleFeature(displayPlan, feature)}
                        />
                        <span class="feature-name">{feature.name}</span>
                        <span class={`feature-category cat-${feature.category}`}>{feature.category}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div class="plan-actions">
                {isEditing ? (
                  <>
                    <button
                      class="btn btn-primary"
                      onClick={() => handleSavePlan(displayPlan)}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      class="btn btn-secondary"
                      onClick={() => setEditingPlan(null)}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      class="btn btn-secondary"
                      onClick={() => setEditingPlan({ ...plan })}
                    >
                      Edit Plan
                    </button>
                    {plan.is_default !== 1 && (
                      <button
                        class="btn btn-danger"
                        onClick={() => handleDeletePlan(plan)}
                      >
                        Delete
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Feature Matrix */}
      <div class="card mt-4">
        <div class="card-header">
          <h2>Feature Matrix</h2>
          {(editingPlan || newPlan) && (
            <span class="badge badge-warning">Showing unsaved changes</span>
          )}
        </div>
        <div class="card-body">
          <div class="feature-matrix-wrapper">
            <table class="feature-matrix">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Category</th>
                  {newPlan && (
                    <th class="new-plan-col">{newPlan.name || 'New Plan'}</th>
                  )}
                  {plans.map(plan => {
                    const isEditing = editingPlan?.id === plan.id;
                    const displayPlan = isEditing ? editingPlan : plan;
                    return (
                      <th key={plan.id} class={isEditing ? 'editing-col' : ''}>
                        {displayPlan.name}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {features.map(feature => (
                  <tr key={feature.id}>
                    <td>
                      <strong>{feature.name}</strong>
                      {feature.description && (
                        <div class="feature-desc">{feature.description}</div>
                      )}
                    </td>
                    <td>
                      <span class={`badge cat-${feature.category}`}>{feature.category}</span>
                    </td>
                    {newPlan && (
                      <td class="text-center new-plan-col">
                        {newPlan.features?.some(f => f.id === feature.id) ? (
                          <span class="check-icon">&#10003;</span>
                        ) : (
                          <span class="dash-icon">&#8212;</span>
                        )}
                      </td>
                    )}
                    {plans.map(plan => {
                      const isEditing = editingPlan?.id === plan.id;
                      const displayPlan = isEditing ? editingPlan : plan;
                      const hasFeature = displayPlan.features?.some(f => f.id === feature.id);
                      return (
                        <td key={plan.id} class={`text-center ${isEditing ? 'editing-col' : ''}`}>
                          {hasFeature ? (
                            <span class="check-icon">&#10003;</span>
                          ) : (
                            <span class="dash-icon">&#8212;</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <style>{`
        .plans-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .plan-card {
          background: white;
          border-radius: 12px;
          border: 2px solid #E5E7EB;
          padding: 1.5rem;
          transition: all 0.2s;
          position: relative;
        }

        .reorder-controls {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.25rem;
          background: #F3F4F6;
          border-radius: 6px;
          padding: 0.25rem;
        }

        .reorder-btn {
          background: white;
          border: 1px solid #D1D5DB;
          border-radius: 4px;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 10px;
          color: #6B7280;
          transition: all 0.2s;
        }

        .reorder-btn:hover:not(:disabled) {
          background: #4F46E5;
          color: white;
          border-color: #4F46E5;
        }

        .reorder-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .sort-index {
          font-size: 11px;
          font-weight: 600;
          color: #6B7280;
          min-width: 16px;
          text-align: center;
        }

        .plan-card.editing {
          border-color: #4F46E5;
          box-shadow: 0 4px 12px rgba(79, 70, 229, 0.15);
        }

        .plan-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .plan-header h3 {
          margin: 0;
          font-size: 1.25rem;
        }

        .plan-name-input {
          font-size: 1.25rem;
          font-weight: 600;
          border: 1px solid #D1D5DB;
          border-radius: 6px;
          padding: 0.5rem;
          width: 100%;
        }

        .plan-pricing {
          margin-bottom: 1rem;
        }

        .price {
          font-size: 1.5rem;
          font-weight: 700;
          color: #111827;
        }

        .price-yearly {
          font-size: 0.875rem;
          color: #6B7280;
        }

        .price-inputs {
          display: flex;
          gap: 1rem;
        }

        .price-input-group {
          flex: 1;
        }

        .price-input-group label {
          display: block;
          font-size: 0.75rem;
          color: #6B7280;
          margin-bottom: 0.25rem;
        }

        .input-with-prefix {
          display: flex;
          align-items: center;
          border: 1px solid #D1D5DB;
          border-radius: 6px;
          overflow: hidden;
        }

        .input-with-prefix span {
          padding: 0.5rem;
          background: #F3F4F6;
          font-size: 0.75rem;
          color: #6B7280;
        }

        .input-with-prefix input {
          border: none;
          padding: 0.5rem;
          width: 100%;
          font-size: 1rem;
        }

        .plan-seats {
          padding: 0.75rem 0;
          border-top: 1px solid #E5E7EB;
          border-bottom: 1px solid #E5E7EB;
          margin-bottom: 1rem;
          color: #4B5563;
        }

        .seats-input-group {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .seats-input-group label {
          font-size: 0.75rem;
          color: #6B7280;
        }

        .seats-input-group input {
          border: 1px solid #D1D5DB;
          border-radius: 6px;
          padding: 0.5rem;
          width: 100px;
        }

        .seats-input-group .hint {
          font-size: 0.7rem;
          color: #9CA3AF;
        }

        .plan-features h4 {
          font-size: 0.875rem;
          color: #374151;
          margin-bottom: 0.75rem;
        }

        .feature-list {
          max-height: 300px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .feature-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem;
          border-radius: 6px;
          cursor: pointer;
          transition: background 0.2s;
        }

        .feature-item:hover {
          background: #F3F4F6;
        }

        .feature-item.active {
          background: #EEF2FF;
        }

        .feature-item input[type="checkbox"] {
          width: 16px;
          height: 16px;
        }

        .feature-name {
          flex: 1;
          font-size: 0.875rem;
        }

        .feature-category {
          font-size: 0.625rem;
          padding: 0.125rem 0.375rem;
          border-radius: 4px;
          text-transform: uppercase;
        }

        .cat-core { background: #DBEAFE; color: #1E40AF; }
        .cat-reporting { background: #D1FAE5; color: #065F46; }
        .cat-advanced { background: #FEF3C7; color: #92400E; }
        .cat-integration { background: #E0E7FF; color: #3730A3; }
        .cat-general { background: #F3F4F6; color: #374151; }

        .plan-actions {
          margin-top: 1rem;
          display: flex;
          gap: 0.5rem;
        }

        .plan-actions .btn {
          flex: 1;
        }

        .feature-matrix-wrapper {
          overflow-x: auto;
        }

        .feature-matrix {
          width: 100%;
          border-collapse: collapse;
        }

        .feature-matrix th,
        .feature-matrix td {
          padding: 0.75rem;
          border-bottom: 1px solid #E5E7EB;
          text-align: left;
        }

        .feature-matrix th {
          background: #F9FAFB;
          font-weight: 600;
          white-space: nowrap;
        }

        .feature-matrix .feature-desc {
          font-size: 0.75rem;
          color: #6B7280;
          margin-top: 0.25rem;
        }

        .check-icon {
          color: #10B981;
          font-size: 1.25rem;
          font-weight: bold;
        }

        .dash-icon {
          color: #D1D5DB;
          font-size: 1rem;
        }

        .header-actions {
          display: flex;
          gap: 0.5rem;
        }

        .new-plan {
          border-color: #10B981;
          background: #ECFDF5;
        }

        .plan-key,
        .plan-description {
          margin-bottom: 1rem;
        }

        .plan-key label,
        .plan-description label {
          display: block;
          font-size: 0.75rem;
          color: #6B7280;
          margin-bottom: 0.25rem;
        }

        .plan-key input,
        .plan-description input {
          width: 100%;
          padding: 0.5rem;
          border: 1px solid #D1D5DB;
          border-radius: 6px;
          font-size: 0.875rem;
        }

        .badge-info {
          background: #10B981;
          color: white;
        }

        .btn-danger {
          background: #EF4444;
          color: white;
          border: none;
        }

        .btn-danger:hover {
          background: #DC2626;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .badge-warning {
          background: #F59E0B;
          color: white;
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
        }

        .new-plan-col {
          background: #ECFDF5 !important;
          border-left: 2px solid #10B981;
          border-right: 2px solid #10B981;
        }

        .editing-col {
          background: #EEF2FF !important;
          border-left: 2px solid #4F46E5;
          border-right: 2px solid #4F46E5;
        }

        thead .new-plan-col {
          border-top: 2px solid #10B981;
        }

        thead .editing-col {
          border-top: 2px solid #4F46E5;
        }

        tbody tr:last-child .new-plan-col {
          border-bottom: 2px solid #10B981;
        }

        tbody tr:last-child .editing-col {
          border-bottom: 2px solid #4F46E5;
        }
      `}</style>
    </div>
  );
}
